import { Injectable } from '@angular/core';
import {
  HttpRequest,
  HttpHandler,
  HttpEvent,
  HttpInterceptor,
  HttpResponse,
  HttpHeaders,
} from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { tap } from 'rxjs/operators';

const CACHE_TTL_MS = 60 * 60 * 1000;
export const GROUP_CACHE_STORAGE_PREFIX = 'ekowitryna_group_cache_';

interface CacheEntry {
  response: HttpResponse<unknown>;
  expiresAt: number;
}

interface SerializedEntry {
  body: unknown;
  status: number;
  statusText: string;
  headers: Record<string, string>;
  expiresAt: number;
}

export function clearGroupHttpCache(): void {
  try {
    for (let i = sessionStorage.length - 1; i >= 0; i--) {
      const key = sessionStorage.key(i);
      if (key?.startsWith(GROUP_CACHE_STORAGE_PREFIX)) {
        sessionStorage.removeItem(key);
      }
    }
  } catch {
    // sessionStorage niedostępny
  }
}

@Injectable({ providedIn: 'root' })
export class GroupCacheInterceptor implements HttpInterceptor {
  private readonly memoryCache = new Map<string, CacheEntry>();

  clearAllCaches(): void {
    this.memoryCache.clear();
    clearGroupHttpCache();
  }

  clearMemoryCache(): void {
    this.memoryCache.clear();
  }

  intercept(
    req: HttpRequest<unknown>,
    next: HttpHandler
  ): Observable<HttpEvent<unknown>> {
    if (req.method !== 'GET' || !this.isGroupRequest(req)) {
      return next.handle(req);
    }

    const key = req.urlWithParams;
    const storageKey = this.toStorageKey(key);

    const memoryEntry = this.memoryCache.get(key);
    if (memoryEntry && memoryEntry.expiresAt > Date.now()) {
      return of(memoryEntry.response.clone());
    }

    const stored = this.getFromSessionStorage(storageKey);
    if (stored) {
      const response = this.deserializeResponse(stored);
      this.memoryCache.set(key, {
        response,
        expiresAt: stored.expiresAt,
      });
      return of(response);
    }

    return next.handle(req).pipe(
      tap((event) => {
        if (event instanceof HttpResponse && event.status === 200) {
          const expiresAt = Date.now() + CACHE_TTL_MS;
          this.memoryCache.set(key, {
            response: event.clone(),
            expiresAt,
          });
          this.setSessionStorage(storageKey, event, expiresAt);
        }
      })
    );
  }

  private toStorageKey(urlKey: string): string {
    try {
      const safe = btoa(encodeURIComponent(urlKey)).replace(/[/+=]/g, '_');
      return GROUP_CACHE_STORAGE_PREFIX + safe;
    } catch {
      return GROUP_CACHE_STORAGE_PREFIX + urlKey.replace(/\W/g, '_').slice(0, 100);
    }
  }

  private getFromSessionStorage(storageKey: string): SerializedEntry | null {
    try {
      const raw = sessionStorage.getItem(storageKey);
      if (!raw) return null;
      const entry: SerializedEntry = JSON.parse(raw);
      if (entry.expiresAt <= Date.now()) {
        sessionStorage.removeItem(storageKey);
        return null;
      }
      return entry;
    } catch {
      return null;
    }
  }

  private setSessionStorage(
    storageKey: string,
    response: HttpResponse<unknown>,
    expiresAt: number
  ): void {
    try {
      const headers: Record<string, string> = {};
      response.headers.keys().forEach((k) => {
        const v = response.headers.get(k);
        if (v != null) headers[k] = v;
      });
      const entry: SerializedEntry = {
        body: response.body,
        status: response.status,
        statusText: response.statusText,
        headers,
        expiresAt,
      };
      sessionStorage.setItem(storageKey, JSON.stringify(entry));
    } catch {
      // sessionStorage pełny lub niedostępny
    }
  }

  private deserializeResponse(entry: SerializedEntry): HttpResponse<unknown> {
    const headers = new HttpHeaders(entry.headers);
    return new HttpResponse({
      body: entry.body,
      status: entry.status,
      statusText: entry.statusText,
      headers,
    });
  }

  private isGroupRequest(req: HttpRequest<unknown>): boolean {
    try {
      const url = new URL(req.url);
      const path = url.pathname;
      return path.endsWith('/group') || path.endsWith('/group/');
    } catch {
      return req.url.includes('/group');
    }
  }
}
